'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import {
  checkFormStep1,
  checkFormStep2,
  checkFormStep3,
} from '~/utils/client/form-validation';
import { InputItems } from '~/types/entry-members-form';
import { MajorLicense } from '~/types/major-license';
import { Experience } from '~/types/experience';
import StepBar from '~/components/shared/horizontal-slide/step-bar';
import Step1 from '~/components/shared/horizontal-slide/step1';
import Step2 from '~/components/shared/horizontal-slide/step2';
import Step3 from '~/components/shared/horizontal-slide/step3';
import { createEntryMember } from '~/lib/applicant';

interface HorizontalSlideFormProps {
  jobId: number;
  jobTitle?: string;
  majorLicenses: MajorLicense[];
  experiences: Experience[];
  areasWithPrefectures: any[];
  prefectures: any[];
  isMobile: boolean;
}

export default function HorizontalSlideForm({
  jobId,
  jobTitle,
  majorLicenses,
  experiences,
  areasWithPrefectures,
  prefectures,
  isMobile,
}: HorizontalSlideFormProps) {
  const [step, setStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState({
    desired_workplace_id: '',
    birth_year: '',
    name: '',
    tel: '',
    email: '',
    license: '',
    experience: '',
  });

  const [inputItems, setInputItems] = useState<InputItems>({
    license: [],
    experience: [],
    desired_workplace_id: [],
    birth_year: '',
    name: '',
    tel: '',
    email: '',
    password: '',
    password_conf: '',
  });
  const path = usePathname();
  const isServicePath = path.includes('service');

  useEffect(() => {
    const formItems = document.querySelectorAll(
      'form input, form textarea, form select',
    );
    const submitButton = document.querySelector('#confirmation_submit_button');
    let isFormChanged = false;

    if (formItems.length === 0 || !submitButton) return;

    const handleChange = () => {
      isFormChanged = true;
    };

    const handleClick = () => {
      isFormChanged = false;
    };

    formItems.forEach((item) => {
      item.addEventListener('change', handleChange);
    });

    submitButton.addEventListener('click', handleClick);

    window.onbeforeunload = (e) => {
      if (isFormChanged) {
        const dialogText =
          'ページを移動しようとしています。\n入力した内容が消えてしまいますがよろしいですか？';
        e.returnValue = dialogText;
        return dialogText;
      }
      return null;
    };

    return () => {
      formItems.forEach((item) => {
        item.removeEventListener('change', handleChange);
      });
      submitButton.removeEventListener('click', handleClick);
      window.onbeforeunload = null;
    };
  }, []);

  const handleNextStep = async (input: InputItems) => {
    setInputItems(input);
    if (step === 1) {
      const { hasErrors, errorMessage } = checkFormStep1(
        input.license,
        input.experience,
      );
      setErrorMessage((prev) => ({ ...prev, ...errorMessage }));
      if (!hasErrors) setStep(2);
    } else if (step === 2) {
      const { hasErrors, errorMessage } = checkFormStep2(
        input.desired_workplace_id,
        input.birth_year,
        input.name,
      );
      setErrorMessage((prev) => ({ ...prev, ...errorMessage }));
      if (!hasErrors) setStep(3);
    }
  };

  const onSubmit = async (input: InputItems) => {
    setInputItems(input);
    const { hasErrors, errorMessage } = checkFormStep3(input.email, input.tel);
    setErrorMessage((prev) => ({ ...prev, ...errorMessage }));
    if (!hasErrors) {
      const redirectUrl = await createEntryMember(jobId, input);
      window.location.href = redirectUrl;
    }
  };

  const titleClass = isServicePath && isMobile ? 'u-fs16' : '';

  return (
    <form
      id="entry-member-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (step === 3) {
          onSubmit(inputItems);
        }
      }}
    >
      <div id="entry_form">
        <div className="u-flex -justify-center">
          <div className="u-flex -items-center u-gap12">
            <div className="c-badge">
              <span className="c-badge__text-secondary">簡単</span>
              <span className="c-badge__text-primary">1分</span>
            </div>
            <h2 className={`p-horizontal-slide__title ${titleClass}`}>
              {isServicePath
                ? '登録して非公開求人を受けとる'
                : '情報を入力して求人に応募'}
            </h2>
          </div>
        </div>

        {jobTitle && (
          <p className="p-horizontal-slide__job-text u-truncate">
            <b>応募求人：</b>
            {jobTitle.length > 30 ? `${jobTitle.slice(0, 30)}...` : jobTitle}
          </p>
        )}

        <div className="u-mt20">
          <StepBar step={step} />
        </div>

        <div className="p-horizontal-slide">
          <div className="p-horizontal-slide__steps" data-step={step}>
            <div className="p-horizontal-slide__step">
              <Step1
                majorLicenses={majorLicenses}
                experiences={experiences}
                isMobile={isMobile}
                inputItems={inputItems}
                licenseErrorMessage={errorMessage.license}
                experienceErrorMessage={errorMessage.experience}
                onNext={(input: InputItems) => handleNextStep(input)}
              />
            </div>
            <div className="p-horizontal-slide__step">
              <Step2
                isMobile={isMobile}
                areasWithPrefectures={areasWithPrefectures}
                prefectures={prefectures}
                inputItems={inputItems}
                prefectureErrorMessage={errorMessage.desired_workplace_id}
                birthYearErrorMessage={errorMessage.birth_year}
                nameErrorMessage={errorMessage.name}
                onBack={() => setStep(1)}
                onNext={(input: InputItems) => handleNextStep(input)}
              />
            </div>
            <div className="p-horizontal-slide__step">
              <Step3
                isMobile={isMobile}
                inputItems={inputItems}
                telErrorMessage={errorMessage.tel}
                emailErrorMessage={errorMessage.email}
                onBack={() => setStep(2)}
                onChangeTel={(tel: string) =>
                  setInputItems((prev) => ({ ...prev, tel }))
                }
                onChangeEmail={(email: string) =>
                  setInputItems((prev) => ({ ...prev, email }))
                }
              />
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
